import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { dynamicConfigStringListIncludes } from '@atlaskit/tmp-editor-statsig/dynamic-config-value-contains';

import { getHeadingContext } from './headingUtils';

/** Default minimum words when `platform_editor_ai_autocomplete_ux_config` has no word-count token. */
const MIN_DOCUMENT_WORDS = 400;

// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
const WHITESPACE_SPLIT_REGEX = /\s+/u;
/** On Enter: dismiss if the cursor block exceeds this length (non-heading). */
const AUTO_DISMISS_MAX_CHARS_NON_HEADING = 20;
/** On Enter: dismiss if the cursor block exceeds this length (heading). */
const AUTO_DISMISS_MAX_CHARS_HEADING = 50;

const AI_AUTOCOMPLETE_UX_DYNAMIC_CONFIG = 'platform_editor_ai_autocomplete_ux_config';

/**
 * Minimum words in the whole document before autocomplete trigger may fire.
 * Driven by Statsig dynamic config `platform_editor_ai_autocomplete_ux_config`
 * string list (`value` key); first matching token wins if several are present.
 */
function getMinDocumentWords(): number {
	if (dynamicConfigStringListIncludes(AI_AUTOCOMPLETE_UX_DYNAMIC_CONFIG, 'word-count-300')) {
		return 300;
	}
	if (dynamicConfigStringListIncludes(AI_AUTOCOMPLETE_UX_DYNAMIC_CONFIG, 'word-count-200')) {
		return 200;
	}
	if (dynamicConfigStringListIncludes(AI_AUTOCOMPLETE_UX_DYNAMIC_CONFIG, 'word-count-350')) {
		return 350;
	}
	return MIN_DOCUMENT_WORDS;
}

/**
 * Serializes the document to plain text (block boundaries → newline, leaf gaps → space).
 */
export function getDocumentPlainText(doc: PMNode): string {
	return doc.textBetween(0, doc.content.size, '\n', ' ');
}

/**
 * Word count for the full document, aligned with editor analytics style:
 * normalize whitespace, then `Intl.Segmenter` word + `isWordLike`, else whitespace split.
 */
function countWordsInDocument(doc: PMNode): number {
	// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
	const normalized = getDocumentPlainText(doc).replace(/\s+/gu, ' ').trim();
	if (!normalized) {
		return 0;
	}
	if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
		try {
			const seg = new Intl.Segmenter('und', { granularity: 'word' });
			let n = 0;
			for (const part of seg.segment(normalized)) {
				if (part.isWordLike) {
					n++;
				}
			}
			return n;
		} catch {
			// fall through
		}
	}
	// @ts-ignore - TS1501 TypeScript 5.9.2 upgrade
	return normalized.split(WHITESPACE_SPLIT_REGEX).length;
}

export function exceedsNodeCharLimit(state: EditorState): boolean {
	const inHeading = getHeadingContext(state).cursorInHeading;
	const len = state.selection.$from.parent.textContent.length;
	const maxChars = inHeading ? AUTO_DISMISS_MAX_CHARS_HEADING : AUTO_DISMISS_MAX_CHARS_NON_HEADING;
	return len > maxChars;
}

export function exceedsMinDocumentWords(state: EditorState): boolean {
	const wordCount = countWordsInDocument(state.doc);
	return wordCount >= getMinDocumentWords();
}
